/**
 * What the WiFi card's ONE Apply does, in the order it must run.
 *
 * Operator, 2026-10-09: "remove the save button next to access vlan, the
 * single apply button for the card should do all the saving/writing". The same
 * evening Franklin Kibowen - 798's WiFi 6 showed why it matters: "Apply to
 * both" named it "Mymanga Public WiFi" and opened it, and left it OFF and on no
 * VLAN, because the mirror copied only the name and the security and the VLAN
 * had its own Save on the 2.4 GHz network alone.
 *
 * The steps:
 *  1. this network's VLAN — put on the chosen VLAN (`bind`: the record, the
 *     chassis, and on a non-ZTE ONT the router's bridge), or taken off the one
 *     it is on when LAN is chosen (`detach`, which also switches it off);
 *  2. with "Apply to both", the paired radio put on the SAME VLAN, where it is
 *     not already;
 *  3. the CPE writes — name, key, security — and, with "Apply to both", the
 *     paired radio switched ON when this one is on.
 * The writes come LAST so an open network is never switched on before it is on
 * its VLAN: on these routers a network bound to nothing joins the subscriber's
 * own LAN, and the card stops at a bind that did not hold. Mirroring never
 * switches the paired radio off and never takes it off a VLAN.
 */

export interface WifiWrite {
  path: string;
  value: string | number | boolean;
}

/** How a network joins: the subscriber's LAN, or bridged onto a VLAN. */
export interface WifiNetworkChoice {
  mode: 'lan' | 'vlan';
  /** The VLAN id as the picker holds it; '' with LAN. */
  vlan: string;
}

export interface WifiCardApplyInput {
  instance: string;
  label: string;
  /** This network on the router now. */
  enabled: boolean | null;
  network: { baseline: WifiNetworkChoice; draft: WifiNetworkChoice };
  /** The paired radio when "Apply to both" is ticked, else null. */
  mirror: {
    instance: string;
    label: string;
    enablePath: string | null;
    enabled: boolean | null;
    /** How the paired radio joins today, from its own record. */
    network: WifiNetworkChoice;
  } | null;
  /** Name / key / security writes the operator changed (the paired radio's copies included). */
  writes: WifiWrite[];
  /** What a Re-apply re-sends when nothing changed. */
  resendWrites: WifiWrite[];
}

export type WifiCardStep =
  | { kind: 'bind'; instance: string; label: string; vlanId: number }
  | { kind: 'detach'; instance: string; label: string }
  | { kind: 'write'; writes: WifiWrite[] };

export interface WifiCardApplyPlan {
  steps: WifiCardStep[];
  /** Something would change — the button says Apply; otherwise it re-sends (Re-apply). */
  changes: boolean;
  /** The operator's own edits (not what ticking "Apply to both" alone implies) — the unsaved-draft guard. */
  edited: boolean;
}

const vlanOf = (choice: WifiNetworkChoice): number | null =>
  choice.mode === 'vlan' && /^\d+$/.test(choice.vlan) ? Number(choice.vlan) : null;

export function networkChanged(network: WifiCardApplyInput['network']): boolean {
  const { baseline, draft } = network;
  if (draft.mode !== baseline.mode) return true;
  return draft.mode === 'vlan' && draft.vlan !== baseline.vlan;
}

export function planWifiCardApply(input: WifiCardApplyInput): WifiCardApplyPlan {
  const steps: WifiCardStep[] = [];
  const { draft, baseline } = input.network;
  const target = vlanOf(draft);
  const ownChange = networkChanged(input.network);
  let detaching = false;

  if (ownChange) {
    if (target !== null) {
      steps.push({ kind: 'bind', instance: input.instance, label: input.label, vlanId: target });
    } else if (draft.mode === 'lan' && baseline.mode === 'vlan') {
      steps.push({ kind: 'detach', instance: input.instance, label: input.label });
      detaching = true;
    }
  }

  const m = input.mirror;
  if (m && target !== null && vlanOf(m.network) !== target) {
    steps.push({ kind: 'bind', instance: m.instance, label: m.label, vlanId: target });
  }

  const writes = [...input.writes];
  // The paired radio comes up with this one — never on a detach, which switches this one off.
  if (m && m.enablePath && input.enabled === true && m.enabled !== true && !detaching) {
    writes.push({ path: m.enablePath, value: true });
  }

  const changes = steps.length > 0 || writes.length > 0;
  if (writes.length > 0) steps.push({ kind: 'write', writes });
  else if (!changes && input.resendWrites.length > 0) steps.push({ kind: 'write', writes: input.resendWrites });

  return { steps, changes, edited: ownChange || input.writes.length > 0 };
}
